import './style.css';
import { Vector } from './vector';
import { Fish, resetFishRng, setFishRng, setShowBehavior } from './fish';
import type { World } from './fish';
import { mountMenu } from './menu';
import type { MenuHandle } from './menu';
import { SPECIES_META } from './state';
import type { Config, SpeciesConfig, Wall } from './state';
import {
    WALLS_MAX,
    WALL_COORD_MAX,
    WALL_COORD_MIN,
    applySceneConfig,
    getConfig,
    setConfig,
    subscribe,
} from './state';
import type { Segment } from './geometry';
import { rngFromSeed } from './rng';
import { buildShareUrl, decodeScene, encodeScene } from './scene';
import { downloadSvg, exportSvg } from './svg-export';
import { runLifecycle } from './lifecycle';
import { countBehaviors, formatCensus } from './census';
import type { Species } from './types';

export const WALL_FORCE_RANGE = 80;
export const WALL_FORCE_STRENGTH = 4;
export const WALL_MIN_LENGTH = 8;

export function wallToSegment(w: Wall): Segment {
    return { x1: w[0], y1: w[1], x2: w[2], y2: w[3] };
}

function clampWallCoord(v: number): number {
    if (!Number.isFinite(v)) return 0;
    if (v < WALL_COORD_MIN) return WALL_COORD_MIN;
    if (v > WALL_COORD_MAX) return WALL_COORD_MAX;
    return v;
}

export function sanitizeWall(wall: Wall): Wall {
    return [
        clampWallCoord(wall[0]),
        clampWallCoord(wall[1]),
        clampWallCoord(wall[2]),
        clampWallCoord(wall[3]),
    ];
}

export const ALPHA_STEP = 0.032;
export const NUM_FISH_CAP = 200;
export const NUM_FISH_PER_VIEWPORT = 50;
export const VIEWPORT_DIVISOR = 600;
export const SPECIES_KEYS: readonly Species[] = ['minnow', 'tetra', 'bass', 'pike'];

export function targetFishCount(viewportWidth: number): number {
    const raw = (viewportWidth / VIEWPORT_DIVISOR) * NUM_FISH_PER_VIEWPORT;
    return Math.max(0, Math.min(NUM_FISH_CAP, Math.floor(raw)));
}

export function spawnFish(world: World, count: number, rng: () => number = Math.random): Fish[] {
    const fish: Fish[] = [];
    for (let i = 0; i < count; i++) {
        const mass = 0.5 + rng() * rng() * rng() * rng() * 2;
        fish.push(new Fish({ mass }, rng() * world.width, rng() * world.height));
    }
    return fish;
}

export function spawnSpeciesFish(
    world: World,
    species: Species,
    count: number,
    opts: SpeciesConfig,
    rng: () => number = Math.random,
): Fish[] {
    const meta = SPECIES_META[species];
    const mass = meta.sizeBase * opts.sizeMult;
    const maxspeed = 12 * mass * opts.speedMult;
    const massCap = meta.massCap * opts.sizeMult;
    const fish: Fish[] = [];
    for (let i = 0; i < count; i++) {
        fish.push(
            new Fish(
                {
                    mass,
                    maxspeed,
                    color: meta.color,
                    aggression: opts.aggression,
                    species,
                    growthRate: meta.growthRate,
                    massCap,
                },
                rng() * world.width,
                rng() * world.height,
            ),
        );
    }
    return fish;
}

export function spawnAllSpecies(
    world: World,
    cfg: Config,
    rng: () => number = Math.random,
): Fish[] {
    const fish: Fish[] = [];
    for (const species of SPECIES_KEYS) {
        const opts = cfg.species[species];
        fish.push(...spawnSpeciesFish(world, species, opts.count, opts, rng));
    }
    return fish;
}

export interface StepStats {
    chaseCount: number;
    grown: number;
    absorbed: number;
}

export function stepFrame(
    world: { width: number; height: number; creatures: Fish[] },
    mouse: Vector,
    follow: boolean,
    cfg: Config = getConfig(),
): StepStats {
    const g = cfg.global;
    let chaseCount = 0;
    const wallSegments: Segment[] =
        cfg.walls.length > 0 ? cfg.walls.map((w) => wallToSegment(w)) : [];
    for (const fish of world.creatures) {
        if (follow) fish.follow(mouse, 150);

        const neighbours = fish.look(world.creatures, g.lookRangeMult * fish.mass, Math.PI * 2);

        const friends = neighbours.filter((n) => n.mass < fish.mass * g.predatorRatio);
        if (friends.length > 0) fish.shoal(neighbours, g.shoaling);
        else fish.wander(200, g.wanderJitter);
        fish.boundaries(world, g.boundaryMargin, g.boundaryForce);

        if (wallSegments.length > 0) {
            fish.avoidWalls(wallSegments, WALL_FORCE_RANGE, WALL_FORCE_STRENGTH);
        }

        const bigger = neighbours.filter((n) => n.mass > fish.mass * g.predatorRatio);
        if (bigger.length > 0) fish.avoid(bigger, 300);

        const smaller = neighbours.filter((n) => n.mass < fish.mass * g.preyRatio);
        if (smaller.length > 0) {
            fish.chase(smaller, g.aggression * fish.aggression);
            chaseCount++;
        }

        if (follow) fish.currentBehavior = 'follow';

        fish.update();
    }

    let grown = 0;
    let absorbed = 0;
    if (g.lifecycleEnabled) {
        const dt = g.frameMs / 1000;
        const result = runLifecycle(world.creatures, dt, g.absorbRatio);
        grown = result.grown;
        absorbed = result.absorbed;
    }

    return { chaseCount, grown, absorbed };
}

export interface RuntimeHandle {
    stop: () => void;
    getFrameMs: () => number;
    getAlpha: () => number;
    toggleSlowmo: () => void;
    setSlowmo: (slow: boolean) => void;
    isSlowmo: () => boolean;
    resize: () => void;
    respawn: (count?: number) => void;
    syncSpecies: () => void;
    world: { width: number; height: number; creatures: Fish[] };
    tick: (timestamp: number) => void;
    addWall: (wall: Wall) => void;
    clearWalls: () => void;
    isPencilMode: () => boolean;
    setPencilMode: (active: boolean) => void;
    getSeed: () => string;
    getShareUrl: () => string;
    exportSvg: () => string;
    downloadSvg: () => void;
    getInitialPositions: () => readonly (readonly [number, number])[];
}

export function applySpeciesDelta(
    creatures: Fish[],
    world: World,
    cfg: Config,
    rng: () => number = Math.random,
): { added: number; removed: number } {
    let added = 0;
    let removed = 0;
    for (const sp of SPECIES_KEYS) {
        const target = cfg.species[sp].count;
        let current = 0;
        for (const f of creatures) {
            if (f.species === sp) current++;
        }
        const delta = target - current;
        if (delta > 0) {
            const fresh = spawnSpeciesFish(world, sp, delta, cfg.species[sp], rng);
            creatures.push(...fresh);
            added += delta;
        } else if (delta < 0) {
            let toRemove = -delta;
            for (let i = creatures.length - 1; i >= 0 && toRemove > 0; i--) {
                if (creatures[i]?.species === sp) {
                    creatures.splice(i, 1);
                    toRemove--;
                    removed++;
                }
            }
        }
    }
    return { added, removed };
}

export interface StartOptions {
    canvas: HTMLCanvasElement;
    window: Window;
    document: Document;
    requestAnimationFrame?: (cb: (t: number) => void) => number;
    cancelAnimationFrame?: (id: number) => void;
    now?: () => number;
    rng?: () => number;
    initialFishCount?: number;
    search?: string;
}

export function randomSeed(): string {
    const a = Math.floor(Math.random() * 0xffffffff).toString(36);
    const b = Math.floor(Math.random() * 0xffffffff).toString(36);
    return `${a}${b}`;
}

export function start(opts: StartOptions): RuntimeHandle {
    const { canvas, window: win, document: doc } = opts;
    const raf = opts.requestAnimationFrame ?? win.requestAnimationFrame.bind(win);
    const caf = opts.cancelAnimationFrame ?? win.cancelAnimationFrame.bind(win);
    const now = opts.now ?? (() => performance.now());

    const search = opts.search ?? win.location.search;
    const decoded = decodeScene(search);
    const seedFromUrl = decoded !== null && decoded.seed.length > 0 ? decoded.seed : null;
    const currentSeed: string = seedFromUrl ?? randomSeed();
    if (decoded !== null) {
        applySceneConfig(decoded.cfg);
    }

    const rng = opts.rng ?? rngFromSeed(currentSeed);
    setFishRng(rng);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('2D canvas context unavailable');
    }

    const mouse = new Vector(0, 0);
    const world: { width: number; height: number; creatures: Fish[] } = {
        width: 0,
        height: 0,
        creatures: [],
    };

    let follow = false;
    let mouseDownAt: number | null = null;
    let slowmo = false;
    let pencilHeld = false;
    let pencilToggleMode = false;
    let draftWall: Wall | null = null;
    const initialCfg = getConfig();
    let frameMs = initialCfg.global.frameMs;
    let alpha = 1;
    let lastFrame = -Infinity;
    let lastCensus = -Infinity;
    let rafId: number | null = null;

    const isPencilActive = (): boolean => pencilHeld || pencilToggleMode;

    const computeFrameMsTarget = (cfg: Config): number =>
        slowmo ? cfg.global.frameMs * cfg.global.slowmoFactor : cfg.global.frameMs;

    const onMouseMove = (e: MouseEvent): void => {
        mouse.set(e.clientX, e.clientY);
        if (draftWall !== null) {
            draftWall = [draftWall[0], draftWall[1], e.clientX, e.clientY];
        }
    };
    const isUiTarget = (target: EventTarget | null): boolean => {
        if (!(target instanceof Element)) return false;
        return target.closest('#menu, #menu-toggle, #play, .text') !== null;
    };
    const onMouseDown = (e: MouseEvent): void => {
        if (isPencilActive()) {
            if (isUiTarget(e.target)) return;
            draftWall = [e.clientX, e.clientY, e.clientX, e.clientY];
            return;
        }
        if (isUiTarget(e.target)) return;
        follow = true;
        mouseDownAt = now();
    };
    const onMouseUp = (e: MouseEvent): void => {
        if (draftWall !== null) {
            const [x1, y1] = draftWall;
            const x2 = e.clientX;
            const y2 = e.clientY;
            const dx = x2 - x1;
            const dy = y2 - y1;
            if (Math.sqrt(dx * dx + dy * dy) >= WALL_MIN_LENGTH) {
                const cfg = getConfig();
                if (cfg.walls.length < WALLS_MAX) {
                    const next: Wall[] = [...cfg.walls, sanitizeWall([x1, y1, x2, y2])];
                    setConfig({ walls: next });
                }
            }
            draftWall = null;
            return;
        }
        const wasFollowing = follow;
        follow = false;
        mouseDownAt = null;
        if (!wasFollowing) return;
        if (isUiTarget(e.target)) return;
        const cfg = getConfig();
        setConfig({ global: { showBehavior: !cfg.global.showBehavior } });
    };
    const isEditableTarget = (target: EventTarget | null): boolean => {
        if (!(target instanceof HTMLElement)) return false;
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
        return target.isContentEditable;
    };

    const onKeyDown = (e: KeyboardEvent): void => {
        if (e.key === 'p' || e.key === 'P') {
            if (isEditableTarget(e.target)) return;
            pencilHeld = true;
            return;
        }
        if (e.key === 's' || e.key === 'S') {
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (isEditableTarget(e.target)) return;
            e.preventDefault();
            exportCurrentSvg();
        }
    };
    const onKeyUp = (e: KeyboardEvent): void => {
        if (e.key === 'p' || e.key === 'P') {
            pencilHeld = false;
            return;
        }
        if (e.key === 's' || e.key === 'S') return;
        if (e.key === 'Meta' || e.key === 'Control' || e.key === 'Alt' || e.key === 'Shift') {
            return;
        }
        if (mouseDownAt !== null && now() - mouseDownAt < 500) {
            toggleSlowmo();
            mouseDownAt = null;
        }
    };
    const onTouchStart = (e: TouchEvent): void => {
        const t = e.changedTouches[0];
        if (!t) return;
        if (isUiTarget(e.target)) return;
        follow = true;
        mouse.set(t.clientX, t.clientY);
    };
    const onTouchMove = (e: TouchEvent): void => {
        const t = e.changedTouches[0];
        if (!t) return;
        if (isUiTarget(e.target)) return;
        mouse.set(t.clientX, t.clientY);
        e.preventDefault();
    };
    const onTouchEnd = (e: TouchEvent): void => {
        follow = false;
        if (isUiTarget(e.target)) return;
        if (e.changedTouches.length >= 2) {
            const cfg = getConfig();
            setConfig({ global: { showBehavior: !cfg.global.showBehavior } });
        }
    };
    const onResize = (): void => {
        resize();
    };

    const resize = (): void => {
        world.width = win.innerWidth;
        world.height = win.innerHeight;
        canvas.width = world.width;
        canvas.height = world.height;
    };

    const toggleSlowmo = (): void => {
        slowmo = !slowmo;
    };

    const setSlowmoState = (s: boolean): void => {
        slowmo = s;
    };

    const tick = (timestamp: number): void => {
        const cfg = getConfig();
        const frameMsTarget = computeFrameMsTarget(cfg);

        if (frameMs < frameMsTarget) {
            frameMs = Math.min(frameMsTarget, frameMs + 1);
            alpha = Math.max(0.2, alpha - ALPHA_STEP);
        } else if (frameMs > frameMsTarget) {
            frameMs = Math.max(frameMsTarget, frameMs - 1);
            alpha = Math.min(1, alpha + ALPHA_STEP);
        } else if (!slowmo) {
            alpha = 1;
        }

        if (timestamp - lastFrame < frameMs) {
            return;
        }
        lastFrame = timestamp;

        ctx.globalAlpha = 0.2 + 0.6 * alpha;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.globalAlpha = alpha;

        const stats = stepFrame(world, mouse, follow, cfg);

        for (const fish of world.creatures) {
            fish.draw(ctx);
        }

        if (cfg.walls.length > 0 || draftWall !== null) {
            ctx.save();
            ctx.lineWidth = 2;
            ctx.strokeStyle = '#000000';
            ctx.beginPath();
            for (const w of cfg.walls) {
                ctx.moveTo(w[0], w[1]);
                ctx.lineTo(w[2], w[3]);
            }
            ctx.stroke();
            if (draftWall !== null) {
                ctx.strokeStyle = '#1a0dab';
                ctx.beginPath();
                ctx.moveTo(draftWall[0], draftWall[1]);
                ctx.lineTo(draftWall[2], draftWall[3]);
                ctx.stroke();
            }
            ctx.restore();
        }

        canvas.dataset.testChaseCount = String(stats.chaseCount);
        canvas.dataset.testFishCount = String(world.creatures.length);
        canvas.dataset.testWallCount = String(cfg.walls.length);
        canvas.dataset.testPencilMode = isPencilActive() ? '1' : '0';

        const counts: Record<Species, number> = { minnow: 0, tetra: 0, bass: 0, pike: 0 };
        for (const f of world.creatures) {
            if (f.species !== undefined) counts[f.species]++;
        }
        canvas.dataset.testMinnowCount = String(counts.minnow);
        canvas.dataset.testTetraCount = String(counts.tetra);
        canvas.dataset.testBassCount = String(counts.bass);
        canvas.dataset.testPikeCount = String(counts.pike);

        if (cfg.global.censusEnabled && timestamp - lastCensus >= 1000) {
            updateCensus();
            lastCensus = timestamp;
        }
    };

    const loop = (timestamp: number): void => {
        tick(timestamp);
        rafId = raf(loop);
    };

    win.addEventListener('mousemove', onMouseMove);
    win.addEventListener('mousedown', onMouseDown);
    win.addEventListener('mouseup', onMouseUp);
    win.addEventListener('keydown', onKeyDown);
    win.addEventListener('keyup', onKeyUp);
    win.addEventListener('resize', onResize);
    doc.body.addEventListener('touchstart', onTouchStart, { passive: true });
    doc.body.addEventListener('touchmove', onTouchMove, { passive: false });
    doc.body.addEventListener('touchend', onTouchEnd, { passive: true });

    resize();

    const respawn = (count?: number): void => {
        if (count !== undefined) {
            world.creatures = spawnFish(world, count, rng);
            return;
        }
        world.creatures = spawnAllSpecies(world, getConfig(), rng);
    };

    const syncSpecies = (): void => {
        applySpeciesDelta(world.creatures, world, getConfig(), rng);
    };

    if (opts.initialFishCount !== undefined) {
        world.creatures = spawnFish(world, opts.initialFishCount, rng);
    } else {
        world.creatures = spawnAllSpecies(world, initialCfg, rng);
    }

    const initialPositions: readonly (readonly [number, number])[] = world.creatures.map(
        (f) => [f.location.x, f.location.y] as const,
    );

    const getSeed = (): string => currentSeed;
    const getShareUrl = (): string => {
        const cfg = getConfig();
        const qs = encodeScene(cfg, currentSeed);
        const loc = win.location;
        return buildShareUrl(loc.origin, loc.pathname, qs);
    };
    const exportCurrentSvg = (): void => {
        const svg = exportSvg(world.creatures, world, { walls: getConfig().walls });
        downloadSvg(svg, { document: doc, window: win });
    };
    const copyShareUrl = (): Promise<boolean> => {
        const url = getShareUrl();
        const clipboard = (win.navigator as Partial<Navigator>).clipboard;
        if (clipboard === undefined) return Promise.resolve(false);
        return clipboard.writeText(url).then(
            () => true,
            () => false,
        );
    };

    const toggleEl = doc.getElementById('menu-toggle');
    let menu: MenuHandle | null = null;
    try {
        menu = mountMenu({
            document: doc,
            toggle: toggleEl,
            onCopyShareUrl: () => {
                void copyShareUrl();
            },
            onExportSvg: () => {
                exportCurrentSvg();
            },
        });
    } catch {
        menu = null;
    }

    let censusEl = doc.getElementById('census');
    let censusOwned = false;
    if (!(censusEl instanceof HTMLElement)) {
        const created = doc.createElement('div');
        created.id = 'census';
        created.hidden = true;
        doc.body.appendChild(created);
        censusEl = created;
        censusOwned = true;
    }
    censusEl.hidden = !initialCfg.global.censusEnabled;

    const updateCensus = (): void => {
        if (!(censusEl instanceof HTMLElement)) return;
        const text = formatCensus(countBehaviors(world.creatures));
        if (censusEl.textContent !== text) censusEl.textContent = text;
    };

    setShowBehavior(initialCfg.global.showBehavior);
    interface SpeciesParams {
        count: number;
        sizeMult: number;
        speedMult: number;
    }
    const snapshotSpecies = (cfg: Config): Record<Species, SpeciesParams> => ({
        minnow: {
            count: cfg.species.minnow.count,
            sizeMult: cfg.species.minnow.sizeMult,
            speedMult: cfg.species.minnow.speedMult,
        },
        tetra: {
            count: cfg.species.tetra.count,
            sizeMult: cfg.species.tetra.sizeMult,
            speedMult: cfg.species.tetra.speedMult,
        },
        bass: {
            count: cfg.species.bass.count,
            sizeMult: cfg.species.bass.sizeMult,
            speedMult: cfg.species.bass.speedMult,
        },
        pike: {
            count: cfg.species.pike.count,
            sizeMult: cfg.species.pike.sizeMult,
            speedMult: cfg.species.pike.speedMult,
        },
    });
    const prevSpeciesParams: Record<Species, SpeciesParams> = snapshotSpecies(initialCfg);
    const unsubscribeBehavior = subscribe((cfg) => {
        setShowBehavior(cfg.global.showBehavior);
        let countChanged = false;
        for (const sp of SPECIES_KEYS) {
            const next = cfg.species[sp];
            const prev = prevSpeciesParams[sp];
            if (next.count !== prev.count) countChanged = true;
            if (next.sizeMult !== prev.sizeMult && prev.sizeMult > 0) {
                const ratio = next.sizeMult / prev.sizeMult;
                for (const f of world.creatures) {
                    if (f.species === sp) {
                        f.setMass(f.mass * ratio);
                        f.massCap *= ratio;
                        f.maxspeed *= ratio;
                        f.maxforce /= ratio;
                        f.separationRange *= ratio;
                        f.lookRange *= ratio;
                    }
                }
            }
            if (next.speedMult !== prev.speedMult && prev.speedMult > 0) {
                const ratio = next.speedMult / prev.speedMult;
                for (const f of world.creatures) {
                    if (f.species === sp) {
                        f.maxspeed *= ratio;
                    }
                }
            }
            prev.count = next.count;
            prev.sizeMult = next.sizeMult;
            prev.speedMult = next.speedMult;
        }
        if (countChanged) {
            applySpeciesDelta(world.creatures, world, cfg, rng);
        }
        for (const f of world.creatures) {
            if (f.species !== undefined) f.aggression = cfg.species[f.species].aggression;
        }
        if (censusEl instanceof HTMLElement) {
            const wasHidden = censusEl.hidden;
            const shouldShow = cfg.global.censusEnabled;
            censusEl.hidden = !shouldShow;
            if (shouldShow && wasHidden) {
                updateCensus();
                lastCensus = -Infinity;
            }
        }
    });

    const playLink = doc.getElementById('play');
    let playTimeoutId: number | null = null;
    const onPlayClick = (e: MouseEvent): void => {
        e.preventDefault();
        const text = doc.querySelector<HTMLElement>('.text');
        if (!text) {
            menu?.open();
            return;
        }
        text.style.transition = 'opacity 0.4s';
        text.style.opacity = '0';
        if (playTimeoutId !== null) win.clearTimeout(playTimeoutId);
        playTimeoutId = win.setTimeout(() => {
            playTimeoutId = null;
            text.style.display = 'none';
            menu?.open();
        }, 400);
    };
    if (playLink) {
        playLink.addEventListener('click', onPlayClick);
    }

    rafId = raf(loop);

    const stop = (): void => {
        if (rafId !== null) {
            caf(rafId);
            rafId = null;
        }
        win.removeEventListener('mousemove', onMouseMove);
        win.removeEventListener('mousedown', onMouseDown);
        win.removeEventListener('mouseup', onMouseUp);
        win.removeEventListener('keydown', onKeyDown);
        win.removeEventListener('keyup', onKeyUp);
        win.removeEventListener('resize', onResize);
        doc.body.removeEventListener('touchstart', onTouchStart);
        doc.body.removeEventListener('touchmove', onTouchMove);
        doc.body.removeEventListener('touchend', onTouchEnd);
        unsubscribeBehavior();
        if (playLink) {
            playLink.removeEventListener('click', onPlayClick);
        }
        if (playTimeoutId !== null) {
            win.clearTimeout(playTimeoutId);
            playTimeoutId = null;
        }
        menu?.destroy();
        if (censusOwned && censusEl instanceof HTMLElement) {
            censusEl.remove();
        }
        resetFishRng();
    };

    const addWall = (wall: Wall): void => {
        const cfg = getConfig();
        if (cfg.walls.length >= WALLS_MAX) return;
        setConfig({ walls: [...cfg.walls, sanitizeWall(wall)] });
    };
    const clearWalls = (): void => {
        setConfig({ walls: [] });
    };

    return {
        stop,
        getFrameMs: () => frameMs,
        getAlpha: () => alpha,
        toggleSlowmo,
        setSlowmo: setSlowmoState,
        isSlowmo: () => slowmo,
        resize,
        respawn,
        syncSpecies,
        world,
        tick,
        addWall,
        clearWalls,
        isPencilMode: () => isPencilActive(),
        setPencilMode: (active: boolean) => {
            pencilToggleMode = active;
            if (!active) draftWall = null;
        },
        getSeed,
        getShareUrl,
        exportSvg: () => exportSvg(world.creatures, world, { walls: getConfig().walls }),
        downloadSvg: () => {
            exportCurrentSvg();
        },
        getInitialPositions: () => initialPositions,
    };
}

function bootstrap(): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const canvas = document.getElementById('canvas');
    if (!(canvas instanceof HTMLCanvasElement)) return;
    const handle = start({ canvas, window, document });
    (window as unknown as { __bnff?: RuntimeHandle }).__bnff = handle;
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bootstrap);
    } else {
        bootstrap();
    }
}
